/**
 * supabase — singleton browser client (legacy import surface).
 *
 * **Sprint 28.15b auth-401 fix:** this module previously instantiated its
 * own `createClient(...)` which produced TWO GoTrueClient instances on the
 * page (one here, one in `auth/supabase.ts`). Both wrote to the same
 * storage key `sb-uqtkxmlpoenphznnapsv-auth-token`, racing on token reads
 * and surfaced the console warning:
 *
 *   "Multiple GoTrueClient instances detected ... may produce undefined
 *   behavior when used concurrently under the same storage key."
 *
 * The race caused signed-in users to see 401s on `/api/prompts/refine`,
 * `/api/prompts/graph/explain`, `/api/pollinations/image` etc.
 *
 * Fix: delegate to `auth/supabase.ts`'s `getSupabase()` (the
 * `@jubit-ai/auth-bridge`-backed client). Existing callsites that do
 * `import { supabase } from '../lib/supabase.ts'` keep working unchanged
 * — the `supabase` export now points at the SAME instance everyone else
 * uses.
 *
 * `isAuthConfigured` keeps its semantics (true when env is set + auth
 * bridge can be instantiated).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { createClient } from '@supabase/supabase-js';
import { getSupabase, hasSupabaseConfig } from '../auth/supabase';

export const isAuthConfigured: boolean = hasSupabaseConfig();

// Supabase v2.103+ tightened `validateSupabaseUrl` and rejects
// previous placeholder shapes at construction time. Lazy the
// placeholder client behind a Proxy so module load never triggers
// `createClient(...)` — only first real method call does. Tests that
// import `supabase` transitively (via imageClient/authHeader) without
// VITE_SUPABASE_URL set won't blow up at import time anymore. Real
// callers always gate on `isAuthConfigured` first so the proxy never
// fires in production paths.
function makeLazyPlaceholderClient(): SupabaseClient {
  let real: SupabaseClient | null = null;
  function init(): SupabaseClient {
    if (real) return real;
    real = createClient(
      'https://aaaaaaaaaaaaaaaaaaaa.supabase.co',
      'placeholder-anon-key',
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      },
    );
    return real;
  }
  return new Proxy({} as SupabaseClient, {
    get(_t, prop) {
      const target = init() as unknown as Record<string | symbol, unknown>;
      const val = target[prop];
      return typeof val === 'function'
        ? (val as (...args: unknown[]) => unknown).bind(target)
        : val;
    },
  });
}

/**
 * Single shared client. When env is configured, this is the auth-bridge
 * client from `auth/supabase.ts` (cookie-backed on `.jubit.ai`,
 * localStorage-backed on `jubuddy.com`). When env is missing, returns a
 * lazy placeholder client so legacy imports don't crash at module
 * load — userStore detects the placeholder URL and stays in
 * "auth-disabled" mode.
 */
export const supabase: SupabaseClient = getSupabase() ?? makeLazyPlaceholderClient();
